/**
 * ProjectSubmissionsList — section read-only listant les submissions
 * d'un projet, avec tri client-side canonique (Chunk 9.M.3 / D22, F1).
 *
 * Pourquoi ce composant
 * ----------------------
 * Avant 9.M.3, `ProjectDetailPage.tsx` rendait la liste inline (~40 lignes
 * JSX). Sur un projet à plusieurs submissions, l'ordre serveur (created_at
 * desc) mélangeait `ready` et `pending/flagged`, forçant un scan complet
 * pour identifier "ce qui me reste à faire".
 *
 * 9.M.3 — extraction du JSX dans ce composant + ajout d'un tri client-side
 * via `sortSubmissionsForList` (helper pur, testé séparément). Aucune
 * régression visuelle voulue : on conserve la même structure DOM (section
 * + header + ul/li), les mêmes classes Tailwind, les mêmes clés i18n
 * (`submissions.list.title`, `create_button`, `empty`,
 * `submissions.statuses.*`).
 *
 * Read-only strict
 * -----------------
 * - aucune mutation, aucun PATCH submission
 * - le bouton "+ Nouvelle soumission" pointe toujours vers la route
 *   existante `/projects/:id/submissions/new` (UploadSubmissionPage)
 * - chaque ligne navigue vers `/submissions/:id` via `<Link>` natif
 *   (cohérent avec la version pré-9.M.3 — pas d'ouverture inline,
 *   décision PO Q-F)
 *
 * Provenance : le lien destinataire fait autorité ; un lien historique seul
 * reste visible comme étant à confirmer. Aucun contrat API modifié.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useSubmissionsByProject, type SubmissionRow } from "../../../lib/queries/submissions";

import { sortSubmissionsForList } from "./submissionListSort";

export type ProjectSubmissionsListProps = {
  projectId: string;
};

export function ProjectSubmissionsList(props: ProjectSubmissionsListProps) {
  const { projectId } = props;
  const { t } = useTranslation();
  const submissions = useSubmissionsByProject(projectId);

  // Tri canonique client-side. `useMemo` pour éviter de re-sort à chaque
  // re-render même quand `data.items` est référence-stable (TanStack Query
  // garantit déjà la stabilité — ceci est belt-and-suspenders).
  const sortedItems = useMemo<SubmissionRow[]>(() => {
    const items = submissions.data?.items ?? [];
    return sortSubmissionsForList(items);
  }, [submissions.data?.items]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
            {t("submissions.list.title")}
          </h2>
          {/* Chunk 9.L.8 — microcopy inbound (PDFs reçus des assureurs)
              parité outbound hint sur ProjectQuoteRequestsSection.
              Clarifie mental-model QR ↔ Submissions observé smoke 9.L.6. */}
          <p className="mt-0.5 text-xs text-slate-500">{t("submissions.list.inbound_hint")}</p>
        </div>
        <Link to={`/projects/${projectId}/submissions/new`} className="btn-primary text-sm">
          {t("submissions.list.create_button")}
        </Link>
      </div>
      {submissions.isLoading ? (
        <p className="text-sm text-slate-400">{t("app.loading")}</p>
      ) : sortedItems.length > 0 ? (
        <ul className="divide-y divide-slate-200">
          {sortedItems.map((s) => (
            <li key={s.id} className="flex items-center justify-between py-2.5">
              <div>
                <Link
                  to={`/submissions/${s.id}`}
                  className="text-sm font-medium text-slate-800 hover:underline"
                >
                  {s.insurer_name}
                </Link>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span className="font-mono">{s.insurer_code}</span>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                    {t(`submissions.statuses.${s.status}`)}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{s.created_at.slice(0, 10)}</span>
                  <span aria-hidden="true">·</span>
                  {s.responds_to_recipient_id ? (
                    <span
                      className="bg-brand-50 text-brand-700 inline-flex items-center rounded-full px-2 py-0.5"
                      title={t("submissions.list.linked_quote_request_title")}
                    >
                      {t("submissions.list.linked_quote_request")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">
                      {t(
                        s.quote_request_id
                          ? "submissions.link.legacy_to_confirm"
                          : "submissions.link.unlinked_badge",
                      )}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">{t("submissions.list.empty")}</p>
      )}
    </section>
  );
}
